<template>
  <UserImpersonationBanner />
  <header class="header" :class="{ 'header--internal': store.isInternalAdmin }">
    <!-- This is for the maintenance banner teleport -->
    <div id="nav-bar-header-maintenance-banner" />
    <nav class="nav" :class="{ 'nav--internal': store.isInternalAdmin }">
      <div v-if="isMobile" class="header__mobile-content">
        <button
          v-if="showMobileMenu"
          class="header__mobile-nav-button"
          :class="{ 'header__mobile-nav-button--internal': store.isInternalAdmin }"
          @click="showNavDrawer"
          aria-label="Navigation"
        >
          <KIcon
            v-if="store.isAuthenticated"
            icon="fa-solid fa-bars"
            class="header__mobile-icon"
            :class="{ 'header__mobile-icon--internal': store.isInternalAdmin }"
            size="xl"
          />
        </button>
        <button aria-label="Home" @click="onHome">
          <img
            v-if="!store.isInternalAdmin"
            src="@/assets/images/careersafe-logo-horizontal-1c-white.svg"
            height="33rem"
            alt="CareerSafe Logo"
          />
          <img
            v-else
            src="@/assets/images/career-safe-logo-2x.svg"
            height="33rem"
            alt="CareerSafe Logo"
          />
        </button>
      </div>
      <div
        v-if="showMobileProfile"
        class="header__mobile-profile"
        :class="{ 'header__mobile-profile--internal': store.isInternalAdmin }"
      >
        <RouterLink
          v-if="!store.isInternalAdmin && cartData !== null"
          id="cartLink"
          :to="{ name: 'Cart' }"
          class="header__link header-profile__cart"
        >
          <KIcon icon="fa-solid fa-cart-shopping" size="lg" />
          <span class="sr-only">Cart Navigation Link</span>
        </RouterLink>
        <button
          @click="showProfileNavDrawer"
          aria-label="Profile Navigation"
          class="header__mobile-profile-button"
          :class="{ 'header__mobile-profile-button--internal': store.isInternalAdmin }"
        >
          <KIcon
            icon="fa-solid fa-user-circle"
            class="header__mobile-user-icon"
            :class="{ 'header__mobile-user-icon--internal': store.isInternalAdmin }"
            size="lg"
          />
          <KIcon
            icon="fa-solid fa-caret-down"
            class="header__mobile-user-icon"
            :class="{ 'header__mobile-user-icon--internal': store.isInternalAdmin }"
            size="sm"
          />
        </button>
      </div>
      <div v-if="!isMobile" class="header__content">
        <button id="header-logo" class="header-logo" aria-label="Home" @click="onHome">
          <img
            v-if="!store.isInternalAdmin"
            src="@/assets/images/careersafe-logo-horizontal-1c-white.svg"
            height="40rem"
            alt="CareerSafe Logo"
          />
          <div v-else class="header-logo__internal">
            <img
              src="@/assets/images/career-safe-logo-2x.svg"
              height="32rem"
              alt="CareerSafe Logo"
            />
            <div class="header-logo__internal--subtitle">Internal</div>
          </div>
        </button>
        <div v-for="navItem in userNavInfo" :key="navItem.id" class="header__nav-item">
          <RouterLink
            v-if="showNavItem(navItem)"
            :id="navItem.id"
            :to="navItem.route"
            class="header__link"
            :class="{
              'header__link--internal': store.isInternalAdmin,
              'router-link-active':
                navItem.id == 'gradebook-nav-link' && router.currentRoute.value.name == 'Gradebook'
            }"
          >
            {{ navItem.title }}
          </RouterLink>

          <KDropdown
            v-else-if="showNavItemDropdown(navItem)"
            :id="navItem.id"
            :model="navItem.children"
            :aria-label="navItem.ariaLabel"
            class="header__dropdown"
            text
          >
            <template #icon>
              <div
                class="header__dropdown-label"
                :class="{ 'header__dropdown-label--internal': store.isInternalAdmin }"
              >
                {{ navItem.title }}
              </div>
              <KIcon icon="fa-solid fa-caret-down" class="header__dropdown-icon" size="xs" />
            </template>
          </KDropdown>
        </div>
      </div>
      <div v-if="showProfile" class="header-profile">
        <RouterLink
          v-if="!store.isInternalAdmin && cartData !== null"
          id="cartLink"
          :to="{ name: 'Cart' }"
          class="header__link header-profile__cart"
        >
          <KIcon icon="fa-solid fa-cart-shopping" size="lg" />
          <span class="sr-only">Cart Navigation Link</span>
        </RouterLink>
        <KDropdown
          v-if="showProfile"
          id="manage-profile-options"
          :model="profileNavItem.children"
          :aria-label="profileNavItem.ariaLabel"
          class="header__dropdown"
          :class="{ 'header__dropdown--internal': store.isInternalAdmin }"
          text
        >
          <template #icon>
            <div
              class="header__dropdown-label"
              :class="{ 'header__dropdown-label--internal': store.isInternalAdmin }"
            >
              {{ profileNavItem.title }}
            </div>
            <KIcon
              icon="fa-solid fa-caret-down"
              class="header__dropdown-icon"
              :class="{ 'header__dropdown-label--internal': store.isInternalAdmin }"
              size="xs"
            />
          </template>
          <template #item="{ item }">
            <div class="header__profile-item menu-item-button" :id="item.id" :class="item.class">
              <KIcon v-if="item.icon" :icon="item.icon" class="header__profile-item--icon" />
              {{ item.label }}
            </div>
          </template>
        </KDropdown>
      </div>
    </nav>
  </header>

  <!-- Nav Drawer -->
  <LayoutHeaderNavigationDrawer ref="layoutHeaderNavigationDrawer" />

  <!-- Profile Drawer -->
  <LayoutHeaderProfileDrawer
    ref="layoutHeaderProfileDrawer"
    @start-switch-organization="onOpenSwitchOrganizationModal"
  />

  <!-- Modals -->
  <OrganizationSwitchDashboardModal ref="switchOrganizationModal" />
</template>

<script setup lang="ts">
import { KDropdown, KIcon } from "@k2united/frontend_components";
import { useRouter } from "vue-router";
import { useUserStore } from "@/stores/user";
import { computed, ref, defineAsyncComponent, watch } from "vue";
import { useResponsive } from "@/composables/useResponsive";
import type { UserNavObject } from "@/types";
import useGetCartQuery from "@/api/queries/useGetCartQuery";

const LayoutHeaderNavigationDrawer = defineAsyncComponent(
  () => import("../../components/layout/LayoutHeaderNavigationDrawer.vue")
);
const LayoutHeaderProfileDrawer = defineAsyncComponent(
  () => import("../../components/layout/LayoutHeaderProfileDrawer.vue")
);
const OrganizationSwitchDashboardModal = defineAsyncComponent(
  () => import("../../components/organization/OrganizationSwitchDashboardModal.vue")
);
const UserImpersonationBanner = defineAsyncComponent(
  () => import("../../components/users/UserImpersonationBanner.vue")
);

const { isMobile } = useResponsive();
const store = useUserStore();
const router = useRouter();

const onHome = () => {
  if (store.isPasswordResetRequired) return;

  router.push({ name: "Home" });
};

// Cart Data
const { data: rawCartData, refetch } = useGetCartQuery(store.isAuthenticated);
const cartData = computed(() => {
  if (rawCartData.value && Object.keys(rawCartData.value.data).length > 0) {
    return rawCartData.value.data;
  } else {
    return null;
  }
});

watch(
  () => store.isAuthenticated,
  (isAuthenticated) => {
    if (isAuthenticated) {
      refetch();
    }
  }
);

// Modals
const onOpenSwitchOrganizationModal = () => {
  switchOrganizationModal.value.showModal();
};

// Nav Buttons
const userNavInfo = computed(() => {
  if (store.isInternalAdmin) {
    return store.getInternalNavInfo;
  }
  return store.getNavInfo;
});

const profileNavItem = computed(() => {
  let profileNavInfo = store.getProfileNavInfo;
  if (store.isInternalAdmin) {
    profileNavInfo = store.getInternalProfileNavInfo;
  }

  for (let i = 0; i < profileNavInfo.children.length; i++) {
    if (profileNavInfo.children[i].class === "org-switch-sub-nav-link") {
      profileNavInfo.children[i].command = () => {
        onOpenSwitchOrganizationModal();
      };
    }
  }

  return profileNavInfo;
});

const showMobileMenu = computed(
  () => isMobile.value && store.isAuthenticated && !store.isPasswordResetRequired
);

const showMobileProfile = computed(
  () => isMobile.value && store.isAuthenticated && !store.isPasswordResetRequired
);

const showNavItem = (navItem: UserNavObject) =>
  navItem.children.length === 0 && store.isAuthenticated && !store.isPasswordResetRequired;

const showNavItemDropdown = (navItem: UserNavObject) =>
  navItem.children.length !== 0 && store.isAuthenticated && !store.isPasswordResetRequired;

const showProfile = computed(
  () => store.isAuthenticated && !store.isPasswordResetRequired && !isMobile.value
);

//Mobile Drawer
const layoutHeaderNavigationDrawer = ref();
const layoutHeaderProfileDrawer = ref();
const switchOrganizationModal = ref();

const showNavDrawer = () => {
  layoutHeaderNavigationDrawer.value.showDrawer(true);
};

const showProfileNavDrawer = () => {
  layoutHeaderProfileDrawer.value.showDrawer(true);
};
</script>

<style scoped lang="scss">
button {
  font-family: $font-family-button;
  font-weight: 600;
  &:focus-visible {
    box-shadow:
      0 0 0 2px #ffffff,
      0 0 0 4px $color-shadow-button-focused;
  }
}

.header-logo {
  padding-right: 40px;
  &:focus-visible {
    box-shadow:
      0 0 0 2px #ffffff,
      0 0 0 4px $color-shadow-button-focused;
  }
}

.header-logo__internal {
  align-items: center;
  column-gap: 0.75rem;
  display: flex;
}

.header-logo__internal--subtitle {
  border-left: 1px solid $color-industry-blue-subtle;
  color: $color-industry-blue-subtle;
  padding-left: 0.75rem;
  @include body-bold;
}

.header:not(:has(.maintenance-banner-container)) {
  height: 53px;
}

.nav {
  align-items: center;
  background-color: $color-industry-blue-default;
  display: flex;
  flex-direction: row;
  height: 100%;
  justify-content: space-between;
  padding: 0 0.5rem 0 0.5rem;
  width: 100%;

  &--internal {
    background-color: $color-industry-blue-highlight !important;
    border-bottom: 2px solid $color-industry-blue-default;
  }
}

.header__content {
  align-items: center;
  column-gap: 1.25rem;
  display: flex;
  flex-direction: row;
  height: 100%;
}

.header__mobile-content {
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: space-between;
}

.header__link {
  color: $color-standard-neutral-neutral0;
  display: inline-block;
  font-family: $font-family-button;
  height: 100%;
  padding: 16px 12px;
  text-decoration: none;
  @include body-semi-bold;

  &:focus-visible {
    box-shadow:
      0 0 0 2px $color-border-inverse,
      0 0 0 4px $color-shadow-button-focused;
  }

  &--internal {
    color: $color-text-brand;
    &:focus-visible {
      box-shadow:
        0 0 0 2px $color-border-active,
        0 0 0 4px $color-shadow-button-focused;
    }
  }
}
.header__nav-item {
  height: 100%;

  &:has(.k-dropdown) {
    border-bottom: 2px solid transparent;
  }

  &:hover,
  &:focus {
    background-color: $color-icon-button-hovered;

    &:has(.header__link--internal) {
      background-color: $color-standard-neutral-neutral0;
    }

    &:not(:has(.router-link-active)) {
      border-bottom: 2px solid $color-standard-neutral-neutral0;

      &:has(.header__link--internal) {
        border-bottom-color: $color-border-active;
      }
    }
  }
}

.router-link-active {
  border-bottom: solid $color-standard-neutral-neutral0 4px;

  &.header__link--internal {
    border-bottom-color: $color-border-active;
  }
}

.header__dropdown-label {
  color: $color-standard-neutral-neutral0;
  font-weight: 600;
  padding-right: 10px;
  text-decoration: none;
}

.header__dropdown-label--internal {
  color: $color-text-brand !important;
}

.header__dropdown-icon {
  color: $color-standard-neutral-neutral0;
  padding-bottom: 4px;
}

:deep(.header__dropdown:focus) {
  box-shadow: none;
}

.header__mobile-icon {
  color: $color-standard-neutral-neutral0;
  padding: 0.7rem;
}

.header__mobile-icon--internal {
  color: $color-text-brand !important;
}

.header__mobile-user-icon {
  color: $color-standard-neutral-neutral0;
  padding-right: 0.2rem;
}

.header__mobile-user-icon--internal {
  color: $color-text-brand !important;
}

.k-dropdown {
  height: 100%;

  :deep(.header__dropdown) {
    height: 100%;
    padding: 13px;

    &:focus-visible {
      box-shadow:
        0 0 0 2px #ffffff,
        0 0 0 4px $color-shadow-button-focused;
    }
  }
}

.header-profile,
.header__mobile-profile {
  align-items: center;
  display: flex;
  height: 100%;

  .header__link,
  .k-dropdown {
    &:hover,
    &:focus {
      background-color: $color-icon-button-hovered;

      &:has(.header__dropdown--internal) {
        background-color: $color-standard-neutral-neutral0;
      }
    }
  }

  &-button {
    height: 100%;
    padding: 0 0.5rem;

    &:hover,
    &:focus {
      background-color: $color-icon-button-hovered;
    }

    &--internal {
      &:hover,
      &:focus {
        background-color: $color-standard-neutral-neutral0;
      }
    }
  }
}

.header__profile-item {
  align-items: center;
  column-gap: 0.75rem;
  cursor: pointer;
  display: flex;
  min-width: 11rem;
  padding: 0.75rem;
}

.header__profile-item--icon {
  min-width: 1.25rem;
}

.sub-menu__border {
  border-top: solid 1px $color-border-subtle;
}

.sub-menu__text {
  .k-menu__label {
    color: $color-text-brand;
    font-weight: 600;
  }
}
</style>
